import { useSyncExternalStore } from 'react';

/**
 * Unifikovaný přístup k Window objektu Magic desktopu pro postMessage.
 *
 * - **Normal mode** (Phoenix je iframe uvnitř Magicu): cílem je `window.top` (= Magic desktop).
 * - **Reverse mode** (Phoenix je top-frame, Magic Control iframe je child): cílem je registrovaný
 *   contentWindow control iframu (zapisuje ho `MagicControlBridge` na `onLoad`).
 *
 * Komponenty postující zprávy do Magicu (typicky přes [useLegacyMessaging](./index.ts))
 * by NIKDY neměly použít `window.top` napřímo — nesedlo by to v Phoenix-On-Top režimu.
 */

let magicControlFrame: Window | null = null;
const listeners = new Set<() => void>();

/**
 * Registruje Magic Control iframe Window. Volá `MagicControlBridge` po načtení skrytého
 * control iframu. Hodnotou `null` se registrace zruší (na unmount bridge).
 */
export function registerMagicControlFrame(frame: Window | null): void {
  if (magicControlFrame === frame) return;
  magicControlFrame = frame;
  for (const listener of listeners) listener();
}

/**
 * Vrátí Window, na který se mají posílat postMessage zprávy adresované Magic desktopu.
 * `null` znamená, že Magic není v dosahu (Phoenix standalone bez integrace).
 */
export function getMagicDesktopFrame(): Window | null {
  if (magicControlFrame) return magicControlFrame;
  if (window.top && window.top !== window.self) return window.top;
  return null;
}

/**
 * React hook — vrací aktuální target frame a re-renderuje komponentu, když se změní
 * (např. když se MagicControlBridge mountne nebo unmountne).
 *
 * Pro postMessage uvnitř event handleru (kde re-render není nutný) stačí `getMagicDesktopFrame()`.
 */
export function useMagicDesktopFrame(): Window | null {
  return useSyncExternalStore(subscribe, getMagicDesktopFrame, getMagicDesktopFrame);
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}
